import { Card } from "@mui/material";
import { useEffect, useMemo } from "react";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { CustomGetOpportunityQuery, UserRole } from "generated/graphql";
import ProjectFileExplorerInput from "modules/ProjectFiles/ProjectFileExplorerInput";
import { useGetCompanyLazy } from "hooks/companies/useGetCompanyLazy";
import OrganizationUsersOptions from "modules/organization/OrganizationUsersOptions/OrganizationUsersOptions";
import OrganizationProductTypeIdsOptions from "modules/OrganizationProductTypes/OrganizationProductTypeIdsOptions/OrganizationProductTypeIdsOptions";
type OpportunityForUpdate = NonNullable<CustomGetOpportunityQuery["getOpportunity"]>;

type Props = {
  opportunity: OpportunityForUpdate;
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="baseline" gap={2} py={0.75}>
      <MDTypography variant="caption" color="text">
        {label}
      </MDTypography>
      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{ textAlign: "right", wordBreak: "break-word" }}
      >
        {value || "—"}
      </MDTypography>
    </MDBox>
  );
}

function formatAddress(o: OpportunityForUpdate) {
  const part1 = [o.addressLine1, o.addressLine2].filter(Boolean).join(", ");
  const part2 = [o.addressCity, o.addressState, o.addressZip].filter(Boolean).join(", ");
  const part3 = [o.addressCountry].filter(Boolean).join(", ");
  const merged = [part1, part2, part3].filter(Boolean).join(" • ");
  return merged || "—";
}

function contactDisplayName(c: any) {
  if (!c) return "—";
  const full = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return full || c.name || "—";
}

function extractProductTypeIds(opportunity: OpportunityForUpdate): string[] {
  const raw = (opportunity as any)?.products;
  if (!Array.isArray(raw)) return [];

  const getId = (item: any): string | null => {
    if (!item) return null;
    if (typeof item === "string" || typeof item === "number") return String(item);

    const id =
      item.id ??
      item.productTypeId ??
      item.productId ??
      item.typeId ??
      item.productType?.id ??
      item.product?.id;

    return id != null ? String(id) : null;
  };

  const ids = new Set<string>();
  raw.forEach((item) => {
    const id = getId(item);
    if (id) ids.add(id);
  });

  return Array.from(ids);
}

export default function OpportunityDetailsPanel({ opportunity }: Props) {
  const [getCompany, { company }] = useGetCompanyLazy();

  useEffect(() => {
    if (opportunity.companyId) {
      getCompany({ id: opportunity.companyId });
    }
  }, [getCompany, opportunity.companyId]);

  const resolvedContact = useMemo(() => {
    const contacts: any[] = (company as any)?.contacts || [];
    const contactId = opportunity.contactId;

    const byId = contactId ? contacts.find((c) => String(c?.id) === String(contactId)) : undefined;
    const byPrimaryFlag = contacts.find((c) => c?.primary === true);

    return byId || (company as any)?.primaryContact || byPrimaryFlag || contacts[0] || undefined;
  }, [company, opportunity.contactId]);

  const companyName = (company as any)?.name || opportunity.company?.name || "—";
  const contactName = contactDisplayName(resolvedContact);
  const contactPhone = resolvedContact?.phone || "—";
  const contactEmail = resolvedContact?.email || "—";

  const folderId = opportunity.projectFilesFolderId || "";

  const productTypeIds = useMemo(() => extractProductTypeIds(opportunity), [opportunity]);

  const due = opportunity.dueAt ? new Date(opportunity.dueAt).toLocaleString() : "—";
  const address = formatAddress(opportunity);

  return (
    <Card sx={{ height: "100%", width: "100%" }}>
      <MDBox p={2}>
        <Row label="Due date" value={due} />

        <OrganizationUsersOptions
          queryOverride={{
            first: 1000,
            roles: [UserRole.ADMIN, UserRole.OFFICE_MANAGER, UserRole.SALES_PERSON],
          }}
        >
          {({ organizationUsersOptions, loading }) => {
            const userId = (opportunity as any)?.userId;
            const salesLabel =
              organizationUsersOptions?.find((x) => String(x.value) === String(userId))?.label ||
              (userId ? `User #${userId}` : "—");

            return <Row label="Sales person" value={loading ? "Loading…" : salesLabel} />;
          }}
        </OrganizationUsersOptions>

        <Row label="Address" value={address} />

        <MDBox mt={2}>
          <MDTypography variant="button" fontWeight="medium">
            Contact Info
          </MDTypography>
          <Row label="Company" value={companyName} />
          <Row label="Contact" value={contactName} />
          <Row label="Phone" value={contactPhone} />
          <Row label="Email" value={contactEmail} />
        </MDBox>

        <MDBox mt={2}>
          <MDTypography variant="button" fontWeight="medium">
            Additional Info
          </MDTypography>

          <OrganizationProductTypeIdsOptions sortByName>
            {({ organizationProductTypeIdsOptions, loading }) => {
              const labels =
                productTypeIds.length > 0
                  ? productTypeIds.map((id) => {
                      const opt = organizationProductTypeIdsOptions?.find(
                        (x) => String(x.value) === String(id)
                      );
                      return opt?.label || `Product #${id}`;
                    })
                  : [];

              const seen = new Set<string>();
              const unique = labels.filter((x) => (seen.has(x) ? false : (seen.add(x), true)));

              return (
                <Row
                  label="Products interested"
                  value={loading ? "Loading…" : unique.length ? unique.join(", ") : "—"}
                />
              );
            }}
          </OrganizationProductTypeIdsOptions>

          <ProjectFileExplorerInput
            folderId={folderId}
            showLabel={false}
            submitFn={() => {}}
          />
        </MDBox>
      </MDBox>
    </Card>
  );
}
